import React, { useState, useEffect, useMemo } from 'react';
import PageLayout from '../../components/base/PageLayout';
import useStockStore from '../../stores/useStockStore'; // import store
import { useAuthContext } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useBrokerIntegration } from '../../contexts/BrokerIntegrationContextCompat';
import { useNavigate } from 'react-router-dom';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs, orderBy, limit, doc, getDoc, updateDoc, deleteField, onSnapshot, addDoc, deleteDoc } from 'firebase/firestore';
import { formatDate as formatDateUtil } from '../../utils/dateFormatter';
import unifiedOAuthService from '../../services/brokers/unifiedOAuthService';

import { getTierConfig } from '../../config/tierConfig';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import './qfaa-account.css';
import {
  STANDING_AUTH_STATES,
  daysRemainingInAuthorization,
  deriveStandingAuthState,
  showRevokeStandingAuthorization,
  standingAuthorizationDayCountExplanation,
  standingAuthorizationExpiryForDays,
  standingAuthorizationExpiryInstantForDays,
  standingAuthorizationExpiryLabel,
  standingAuthorizationGrantIsVoid,
  standingAuthorizationSignedLabel,
} from './standingAuthDisplay';
import { readSavedMaxSimultaneous } from './savedMaxSimultaneous';
import { deleteEngagementsForUser } from '../../utils/engagementCleanup';

const SA_DOC_VERSION = '1.0';
// Shown when none of the user's engagement rows carries maxSimultaneous (rows saved
// before engage started writing it). Chris, 2026-10-21: "default to 6 unless the client
// changes to a lower value"; 6 is also the engage form's default.
const MAX_SIMULTANEOUS_DEFAULT = 6;

// UI VISIBILITY KILL SWITCH for the Standing Trading Authorization tab. Adopted
// from PR #193 (fix/hide-unwired-standing-auth), same name, same shape, same
// default, so whichever of the two PRs merges first the switch survives and #193's
// ./standingAuthGate.test.mjs still passes against this file. Resolving that merge
// toward this branch WITHOUT this constant would silently delete the switch.
//
// Why this is true, and what is NOT yet true (2026-08-22, Chris's Monday request):
// Chris asked for the tab to be visible and usable — "we absolutely must tell clients
// to use the trading authorization going forward so it has to work" — so this is on.
// The server-side hold is a SEPARATE switch (STANDING_AUTH_HOLDS_ENGAGEMENT plus a
// per-user allowlist on cleanupEngagements) and at the time of writing it is set for
// NOBODY: `gcloud functions describe cleanupEngagements` carries 10 env vars and none
// of them is STANDING_AUTH_*. So today a client can see this tab, sign it, and still
// have their engagement cleared at 4 PM ET.
//
// That gap is the reason #193 existed. Two things must land with or before this flag
// reaches production, and neither is in this branch:
//   1. the two env vars set on cleanupEngagements for every uid that can see the tab,
//      NOT just Chris's — otherwise ~45 other clients read "Authorized through <date>"
//      and get no hold at all, which is the same false promise in the other direction;
//   2. an engagement ESTABLISHER. Standing authorization only spares an existing row
//      (planEngagementCleanup returns {deletePaths, skipPaths} and nothing else); it
//      never creates one. It therefore does not replace the daily engagement bot on
//      its own, whatever this page implies to a client who signs it.
// If this merges while either is outstanding, the honest move is to flip it back.
//
// DELIBERATELY SEPARATE FROM THE SERVER FLAG, and must stay separate: this decides
// whether a client can SEE and SIGN the consent form, the server flag decides
// whether the 4 PM ET job may SPARE A ROW. They are different questions with
// different blast radii and either can move without the other.
//
// KEEP THIS A STATIC `const` BOUND TO A LITERAL. It is what makes the gate provable,
// in two ways:
//   - Source: every render site is reachable only through this constant or through
//     `saIsActive`, which is derived from it. #193's ./standingAuthGate.test.mjs
//     checks that reachability chain and fails if a new render site stops keying
//     off one of them.
//   - Bundle: because the value is a literal, Rollup constant-folds it and
//     dead-code-eliminates the tab, so the authorization copy is absent from the
//     built JS entirely.
// Turning this into a runtime value (env var, Firestore setting, prop, useState)
// silently voids the second guarantee and the .mjs check will fail to remind you.
// If the feature ever needs a runtime switch, gate the runtime value on top of this
// constant rather than replacing it.
const STANDING_AUTH_ENABLED = true;

// Browser-local, and only still here for the `ended` stamp on a revoked record,
// which is display text no rule reads. The standing-authorization EXPIRY and
// SIGNED fields no longer go through it: both are enforcement-bearing and are now
// written in America/New_York by ./standingAuthDisplay.js, off the same clock the
// card and the 4:00 PM ET cleanup job read. See that module's header.
const formatSaDate = (date) =>
  date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export default function AccountPage() {
  const [activeTab, setActiveTab] = useState('profile');
  const [darkMode, setDarkMode] = useState(true);
  const { isConnected, toggleConnection, stocks } = useStockStore();
  const { user, userProfile, updateUserProfile, updateUserPassword } = useAuthContext();
  // "Maximum Number of Trades Simultaneously" as saved on the user's engagement rows
  // (tbl_engagement.maxSimultaneous), so what the client signs is what they engaged
  // with, on any browser. null = no row carries it. Signing waits for the read
  // ('ok'): a pending or failed read would otherwise sign the fallback.
  const [savedMaxSimultaneous, setSavedMaxSimultaneous] = useState(null);
  const [maxSimRead, setMaxSimRead] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [maxSimAttempt, setMaxSimAttempt] = useState(0);
  useEffect(() => {
    if (!user?.uid) {
      setSavedMaxSimultaneous(null);
      setMaxSimRead('loading');
      return undefined;
    }
    return readSavedMaxSimultaneous(
      () =>
        getDocs(query(collection(db, 'tbl_engagement'), where('userId', '==', user.uid))).then((snap) =>
          snap.docs.map((d) => d.data())
        ),
      ({ value, status }) => {
        setSavedMaxSimultaneous(value);
        setMaxSimRead(status);
      }
    );
  }, [user?.uid, maxSimAttempt]);
  const { notify, billingData } = useNotifications();
  const { brokerIntegrations, deleteBrokerIntegration,refreshIntegrations, expiredConnections, activeConnections } = useBrokerIntegration();
  
  // Debug logging for context data
  console.log('🔍 AccountPage - Context data:', {
    brokerIntegrationsLength: brokerIntegrations?.length,
    activeConnectionsLength: activeConnections?.length,
    expiredConnectionsLength: expiredConnections?.length,
    brokerIntegrations: brokerIntegrations?.map(i => ({ 
      id: i.id, 
      status: i.status, 
      broker: i.broker,
      hasCredentials: !!i.credentials,
      credentialsStatus: i.credentials ? Object.keys(i.credentials) : 'none'
    })),
    activeConnections: activeConnections?.map(i => ({ id: i.id, status: i.status, broker: i.broker })),
    expiredConnections: expiredConnections?.map(i => ({ id: i.id, status: i.status, broker: i.broker }))
  });

  // Debug each integration individually
  brokerIntegrations?.forEach((integration, index) => {
    console.log(`🔍 AccountPage - Integration ${index}:`, {
      id: integration.id,
      status: integration.status,
      broker: integration.broker,
      credentials: integration.credentials ? {
        hasAccessToken: !!(integration.credentials.accessToken || integration.credentials.access_token),
        expiresAt: integration.credentials.expiresAt,
        sessionExpiration: integration.credentials.sessionExpiration
      } : 'no credentials',
      metadata: integration.metadata ? {
        accountType: integration.metadata.accountType,
        expirationDate: integration.metadata.expirationDate
      } : 'no metadata'
    });
  });
  const navigate = useNavigate();
  
  // Initialize profile data from Firebase
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    username: '',
    timezone: 'America/New_York',
    notifications: {
      email: true,
      push: true,
      sms: false,
      tradeAlerts: true,
      weeklyReports: true,
      marketNews: false
    }
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [accountStatuses, setAccountStatuses] = useState({});
  const [accountStatus, setAccountStatus] = useState('Active');
  
  const [billingInfo, setBillingInfo] = useState({
    plan: 'Loading...',
    nextBillingDate: 'Loading...',
    annualFee: '-',
    monthlyFee: '-',
    paymentMethod: '-',
  });
  
  const [billingHistory, setBillingHistory] = useState([]);
  const [billingError, setBillingError] = useState(null);
  
  const [subscriptionData, setSubscriptionData] = useState({
    annual: {
      type: 'Annual Subscription',
      tier: 'Tier 1',
      startDate: '01/15/25',
      renews: '01/15/25',
      paymentStatus: 'Inactive',
      amount: '$12,000',
      autoRenew: 'On'
    },
    monthly: {
      type: 'Monthly Maintenance Fee',
      tier: 'Tier 1',
      startDate: '01/15/25',
      renews: '02/15/25',
      paymentStatus: 'Inactive',
      amount: '$149.00',
      autoRenew: 'On'
    }
  });

  // Force re-render when subscriptionData changes
  const [forceRender, setForceRender] = useState(0);
  useEffect(() => {
    setForceRender(prev => prev + 1);
  }, [subscriptionData]);
  
  const [apiKeys, setApiKeys] = useState([
    { id: 'key_1', name: 'Trading App', created: '01/15/2025', lastUsed: '04/23/2025' }
  ]);

  const [saDays, setSaDays] = useState(30);
  const [saAcks, setSaAcks] = useState([false, false, false, false, false, false]);
  const [saConfirm, setSaConfirm] = useState('');
  const [saSaving, setSaSaving] = useState(false);
  
  // Debug logging removed to prevent infinite re-renders

  // REMOVED: updateFirestoreToInactive function
  // This was causing payment_status to change from Active to Inactive automatically

  // REMOVED: Auto-update Firestore to "Inactive" when component mounts
  // This was causing payment_status to change from Active to Inactive automatically

  // REMOVED: useEffect that was creating duplicate records
  // This was causing multiple records to be created for the same user
  // The hardcoded firstname: 'Rungpilin' and lastname: 'Anantathanavit' 
  // were being added to oscar@volarecap.com records
  
  // Load user profile data when component mounts or userProfile changes
  useEffect(() => {
    if (userProfile) {
      setProfileData({
        name: (userProfile.fullName || '').replace(/^(Mr\.|Mrs\.|Ms\.|Dr\.|Prof\.)\s*/i, ''),
        email: userProfile.email || '',
        phone: userProfile.phoneNumber || '',
        username: userProfile.username || '',
        timezone: userProfile.timezone || 'America/New_York',
        notifications: {
          email: userProfile.notificationPreferences?.emailNotifications ?? true
        }
      });
    }
  }, [userProfile]);

  // Use billing data from NotificationContext (commented out to use mock data)
  /*
  useEffect(() => {
    if (billingData) {
      console.log('📊 Using billing data from NotificationContext:', billingData);
      
      // Update subscription data if available
      if (billingData.subscription) {
        const subscriptionFromContext = billingData.subscription;
        console.log('📊 Subscription from context:', subscriptionFromContext);
        
        // Update subscription data with context data
        const newSubscriptionData = {
          annual: {
            type: 'Annual Subscription',
            tier: subscriptionFromContext.plan || 'Tier 1',
            startDate: subscriptionFromContext.startDate ? formatDate(subscriptionFromContext.startDate) : 'N/A',
            renews: subscriptionFromContext.endDate ? formatDate(subscriptionFromContext.endDate) : 'N/A',
            paymentStatus: subscriptionFromContext.status || 'Active',
            amount: subscriptionFromContext.annualAmount || '$12,000',
            autoRenew: subscriptionFromContext.autoRenew ? 'On' : 'Off'
          },
          monthly: {
            type: 'Monthly Maintenance Fee',
            tier: subscriptionFromContext.plan || 'Tier 1',
            startDate: subscriptionFromContext.startDate ? formatDate(subscriptionFromContext.startDate) : 'N/A',
            renews: subscriptionFromContext.endDate ? formatDate(subscriptionFromContext.endDate) : 'N/A',
            paymentStatus: subscriptionFromContext.status || 'Active',
            amount: subscriptionFromContext.monthlyAmount || '$149.00',
            autoRenew: subscriptionFromContext.autoRenew ? 'On' : 'Off'
          }
        };
        
        setSubscriptionData(newSubscriptionData);
        console.log('✅ Updated subscription data from context:', newSubscriptionData);
      }
      
      // Update billing history if available
      if (billingData.paymentHistory && billingData.paymentHistory.length > 0) {
        const billingHistoryFromContext = billingData.paymentHistory.map(item => ({
          id: item.id,
          date: item.timestamp ? formatDate(item.timestamp) : 'N/A',
          description: item.description || 'Payment',
          amount: item.amount ? `$${item.amount}` : 'N/A',
          status: item.status || 'Paid',
          receipt: item.receipt_url || null
        }));
        
        setBillingHistory(billingHistoryFromContext);
        console.log('✅ Updated billing history from context:', billingHistoryFromContext);
      }
    }
  }, [billingData]);
  */

  // Update subscription data from user profile (commented out to use mock data)
  /*
  useEffect(() => {
    if (userProfile?.subscription) {
      console.log('📊 Using subscription data from user profile:', userProfile.subscription);
      
      const userSub = userProfile.subscription;
      const currentDate = new Date();
      
      // Calculate dates based on user subscription
      let startDate = 'N/A';
      let endDate = 'N/A';
      
      if (userSub.startDate) {
        try {
          // Handle Firebase Timestamp
          let startTimestamp;
          if (userSub.startDate.toDate) {
            startTimestamp = userSub.startDate.toDate();
          } else if (userSub.startDate instanceof Date) {
            startTimestamp = userSub.startDate;
          } else {
            startTimestamp = new Date(userSub.startDate);
          }
          
          if (!isNaN(startTimestamp.getTime())) {
            startDate = formatDate(startTimestamp);
            
            // Calculate end date (1 year from start for annual, 1 month for monthly)
            if (userSub.plan === 'free') {
              endDate = 'N/A'; // Free plan has no end date
            } else {
              const endTimestamp = new Date(startTimestamp);
              endTimestamp.setFullYear(endTimestamp.getFullYear() + 1);
              endDate = formatDate(endTimestamp);
            }
          }
        } catch (error) {
          console.error('Error parsing subscription dates:', error);
        }
      }
      
      const newSubscriptionData = {
        annual: {
          type: 'Annual Subscription',
          tier: userSub.plan === 'free' ? 'Free' : 'Tier 1',
          startDate: startDate,
          renews: endDate,
          paymentStatus: userSub.status || 'Active',
          amount: userSub.plan === 'free' ? 'Free' : '$12,000',
          autoRenew: userSub.plan === 'free' ? 'N/A' : 'On'
        },
        monthly: {
          type: 'Monthly Maintenance Fee',
          tier: userSub.plan === 'free' ? 'Free' : 'Tier 1',
          startDate: startDate,
          renews: endDate,
          paymentStatus: userSub.status || 'Active',
          amount: userSub.plan === 'free' ? 'Free' : '$149.00',
          autoRenew: userSub.plan === 'free' ? 'N/A' : 'On'
        }
      };
      
      setSubscriptionData(newSubscriptionData);
      console.log('✅ Updated subscription data from user profile:', newSubscriptionData);
      console.log('🔍 Current subscriptionData state:', newSubscriptionData);
    }
  }, [userProfile]);
  */
  
  // Fetch subscription data from Firebase (commented out to use mock data)
  const fetchSubscriptionData = async () => {
    // Use mock data instead of fetching from Firebase
    console.log('📋 Using mock subscription data');
    
    // Mock subscription data is already set in useState above
    // No need to fetch from Firebase
  };
  
  // Fetch subscription data when user changes (commented out to use mock data)
  /*
  useEffect(() => {
    console.log('🔄 User changed, fetching subscription data...');
    console.log('👤 Current user:', user?.email);
    fetchSubscriptionData();
  }, [user]);
  */
  
  // ONE-TIME CLEANUP: remove test@mail.com data from payment history and users
  useEffect(() => {
    const FLAG = 'cleanup_test_mail_done';
    if (sessionStorage.getItem(FLAG)) return;

    const runCleanup = async () => {
      try {
        const targetEmail = 'test@mail.com';
        let removed = 0;
        // Delete from payment history by email
        const snapEmail = await getDocs(query(collection(db, 'tbl_stripe_payment_history'), where('email', '==', targetEmail)));
        for (const d of snapEmail.docs) {
          await deleteDoc(doc(db, 'tbl_stripe_payment_history', d.id));
          removed++;
          console.log('🗑️ Deleted payment_history by email id:', d.id);
        }
        // Delete from payment history by customerEmail
        const snapCust = await getDocs(query(collection(db, 'tbl_stripe_payment_history'), where('customerEmail', '==', targetEmail)));
        for (const d of snapCust.docs) {
          await deleteDoc(doc(db, 'tbl_stripe_payment_history', d.id));
          removed++;
          console.log('🗑️ Deleted payment_history by customerEmail id:', d.id);
        }
        // Delete from users collection
        const usersSnap = await getDocs(query(collection(db, 'users'), where('email', '==', targetEmail)));
        for (const d of usersSnap.docs) {
          await deleteDoc(doc(db, 'users', d.id));
          console.log('🗑️ Deleted user doc id:', d.id);
        }
        console.log(`✅ Cleanup complete. Removed ${removed} payment history docs.`);
      } catch (e) {
        console.warn('Cleanup failed:', e);
      } finally {
        sessionStorage.setItem(FLAG, 'true');
      }
    };

    runCleanup();
  }, []);
  
  // Helper: format date from multiple possible shapes (empty when missing)
   const formatDateAny = (dateValue) => {
    if (!dateValue) return '';

    try {
      let date;

      // Handle Firestore Timestamp (has .seconds property)
      if (dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      }
      // Handle ISO string or regular Date object
      else if (typeof dateValue === 'string' || dateValue instanceof Date) {
        date = new Date(dateValue);
      }
      else {
        return '';
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }

      return formatDateUtil(date); // mm/dd/yyyy format
    } catch (error) {
      console.error('Error formatting date:', error, dateValue);
      return '';
    }
  };
  // Helper function to get broker display name
  const getBrokerDisplayName = (broker) => {
    if (!broker) return '—';
    const displayNames = {
      'tradier': 'Tradier',
      'tastytrade': 'TastyTrade',
      'schwab': 'Charles Schwab',
      'interactive_brokers': 'Interactive Brokers'
    };
    return displayNames[broker] || broker.charAt(0).toUpperCase() + broker.slice(1).replace('_', ' ');
  };

  // Helper function to get account number from broker integration
  const getAccountDisplay = (integration) => {
    const accountId = integration?.credentials?.accountId || integration?.credentials?.selectedAccountId;
    if (accountId) {
      return `••••${String(accountId).slice(-4)}`;
    }
    if (integration?.id) {
      return `••••${String(integration.id).slice(-4)}`;
    }
    return '';
  };

  // Helper function to get balance from broker integration
  const getAccountBalance = (integration) => {
    if (integration.metadata?.accountBalance) {
      return `$${integration.metadata.accountBalance.toLocaleString()}`;
    }
    return '';
  };

  // Determine real-time connection/expiration status for badge display
  const parseExpirationDate = (value) => {
    if (!value) return null;
    try {
      if (typeof value === 'string') {
        const str = value.trim();
        const ddmmyyyy = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        const m = str.match(ddmmyyyy);
        if (m) {
          const day = parseInt(m[1], 10);
          const month = parseInt(m[2], 10) - 1;
          const year = parseInt(m[3], 10);
          return new Date(year, month, day);
        }
      }
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const getRealTimeStatus = (integration) => {
    console.log('🔍 getRealTimeStatus for integration:', integration.id, {
      integrationStatus: integration.status,
      hasCredentials: !!integration.credentials,
      hasAccessToken: !!(integration.credentials?.accessToken || integration.credentials?.access_token),
      activeConnections: activeConnections?.length,
      expiredConnections: expiredConnections?.length,
      activeConnectionsIds: activeConnections?.map(i => i.id),
      expiredConnectionsIds: expiredConnections?.map(i => i.id),
      integrationDetails: {
        broker: integration.broker,
        status: integration.status,
        credentials: integration.credentials
      }
    });

    // Issue #44: explicit needs_reauth takes precedence over the expiry heuristic
    // below. Backend has already marked the integration unusable and we want a
    // distinct "Reconnect Required" badge instead of "Expired".
    if (integration.status === 'needs_reauth') {
      return 'needs_reauth';
    }

    // First check if integration is in context lists (most reliable)
    if (expiredConnections?.some((i) => i.id === integration.id)) {
      console.log('🔍 Found in expiredConnections, returning expired');
      return 'expired';
    }
    
    if (activeConnections?.some((i) => i.id === integration.id)) {
      console.log('🔍 Found in activeConnections, returning connected');
      return 'connected';
    }

    // If no context data available, check integration status directly
    if (integration.status === 'active' || integration.status === 'connected') {
      console.log('🔍 Integration status is active/connected, checking token validity');
      
      if (integration.credentials) {
        const hasToken = integration.credentials.accessToken || integration.credentials.access_token;
        if (hasToken) {
          const expiry = parseExpirationDate(integration.credentials.expiresAt) ||
                         parseExpirationDate(integration.credentials.sessionExpiration) ||
                         parseExpirationDate(integration.metadata?.expirationDate);
          
          if (expiry) {
            const now = new Date();
            const isValid = expiry > now;
            console.log('🔍 Token expiry check:', {
              expiry: expiry.toISOString(),
              now: now.toISOString(),
              isValid
            });
            return isValid ? 'connected' : 'expired';
          } else {
            console.log('🔍 No expiry date found, assuming connected');
            return 'connected';
          }
        } else {
          console.log('🔍 No access token found, returning disconnected');
          return 'disconnected';
        }
      } else {
        console.log('🔍 No credentials found, returning disconnected');
        return 'disconnected';
      }
    }

    // Additional fallback: if integration has credentials and no explicit status, assume connected
    if (integration.credentials && (integration.credentials.accessToken || integration.credentials.access_token)) {
      console.log('🔍 Fallback: Integration has valid credentials, assuming connected');
      return 'connected';
    }

    // Final fallback: if integration exists and has any broker type, assume it's connected
    if (integration.broker && integration.broker !== '') {
      console.log('🔍 Final fallback: Integration has broker type, assuming connected:', integration.broker);
      return 'connected';
    }
    
    console.log('🔍 Integration status is not active, returning disconnected');
    return 'disconnected';
  };

  // Helper: resolve expiry date shown in Expires from Broker & Trading first
  const getExpiryDateDisplay = (integration) => {
    // Try to find the integration in context lists for a normalized expirationDate
    const ref = [...(activeConnections || []), ...(expiredConnections || [])].find(i => i.id === integration.id) || integration;
    const expiry = parseExpirationDate(
      ref.credentials?.expiresAt ||
      ref.credentials?.sessionExpiration ||
      ref.metadata?.expirationDate
    );
    return expiry ? formatDateUtil(expiry) : '';
  };
  
  // Fetch subscription data from Firestore when user changes
  useEffect(() => {
    if (!user?.email) {
      console.log('❌ No user email, skipping subscription fetch');
      return;
    }

    console.log('🔍 Fetching subscription data for user:', user.email);

    const fetchSubscriptionData = async () => {
      try {
        // Try to fetch by email field first
        let snap = await getDocs(query(collection(db, 'tbl_stripe_payment_history'), where('email', '==', user.email)));
        
        // If no results, try customerEmail field
        if (snap.empty) {
          console.log('🔍 No results for email, trying customerEmail...');
          snap = await getDocs(query(collection(db, 'tbl_stripe_payment_history'), where('customerEmail', '==', user.email)));
        }

        if (!snap.empty) {
          // Get the most recent document
          const docs = snap.docs.sort((a, b) => b.data().created_at?.toDate() - a.data().created_at?.toDate());
          const picked = docs[0];
          const data = picked.data();
          
          // Debug: show which email/docId the Subscription Details are pulled from
          console.log('✅ Subscription source:', {
            docId: picked.id,
            emailField: data.email,
            customerEmailField: data.customerEmail,
            usedEmail: user.email
          });
          
          // Extract subscription data
          console.log('🔍 Payment status debug:', {
            topLevel: data.payment_status,
            metadata: data.metadata?.payment_status,
            final: data.payment_status || data.metadata?.payment_status || 'Inactive'
          });
          const paymentStatus = data.payment_status || data.metadata?.payment_status || 'Inactive';
          console.log('🔍 Final paymentStatus:', paymentStatus);
          
          // Debug date fields
          console.log('🔍 Date fields debug:', {
            subs_start_date: data.subs_start_date,
            subs_renewal_date: data.subs_renewal_date,
            subs_start_date_type: typeof data.subs_start_date,
            subs_renewal_date_type: typeof data.subs_renewal_date
          });
          
          const startDate = data.subs_start_date ? 
            (data.subs_start_date.toDate ? formatDateUtil(data.subs_start_date.toDate()) : formatDateUtil(new Date(data.subs_start_date))) : 'N/A';
          const renewalDate = data.subs_renewal_date ? 
            (data.subs_renewal_date.toDate ? formatDateUtil(data.subs_renewal_date.toDate()) : formatDateUtil(new Date(data.subs_renewal_date))) : 'N/A';
            
          console.log('🔍 Formatted dates:', {
            startDate: startDate,
            renewalDate: renewalDate
          });
          // Determine plan and amounts
          const autoRenew = data.subs_auto_renew || 'Yes';
          const isFounder = (
            data.planType === 'FOUNDERS' ||
            data.plan_type === 'FOUNDERS' ||
            data.metadata?.planType === 'FOUNDERS' ||
            data.product === 'FOUNDERS' ||
            data.product_details === 'FOUNDERS'
          );

          // Prefer numeric amount fields from payment record
          const rawAnnualAmount = (data.amount ?? data.subs_amount ?? '0');
          const numericAnnual = Number(rawAnnualAmount);
          const annualAmount = isFounder ? 12000 : (Number.isFinite(numericAnnual) ? numericAnnual : 0);

          const formatCurrency = (v) => new Intl.NumberFormat('en-US', {
            style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
          }).format(v);
          
          // Derive tier from payment record
          const tierFromRecord = getTierConfig(data.subs_tier).name;

          // Update subscription data state
          setSubscriptionData({
            annual: {
              type: 'Annual Subscription',
              tier: tierFromRecord,
              startDate: startDate,
              renews: renewalDate,
              paymentStatus: paymentStatus,
              amount: formatCurrency(annualAmount),
              autoRenew: autoRenew
            },
            monthly: {
              type: 'Monthly Maintenance Fee',
              tier: tierFromRecord,
              startDate: startDate,
              renews: renewalDate,
              paymentStatus: paymentStatus,
              amount: formatCurrency(getTierConfig(data.subs_tier).monthly),
              autoRenew: autoRenew
            }
          });
          
          console.log('✅ Updated subscription data from Firestore');
          console.log('🔍 Set paymentStatus to:', paymentStatus);
        } else {
          console.log('❌ No subscription data found for user:', user.email);
        }
      } catch (error) {
        console.error('❌ Error fetching subscription data:', error);
      }
    };

    fetchSubscriptionData();
  }, [user?.email]);

  // Update billing info from subscription data
  useEffect(() => {
    if (!subscriptionData?.annual) return;
    setBillingInfo({
      plan: subscriptionData.annual.tier,
      nextBillingDate: subscriptionData.annual.renews,
      annualFee: subscriptionData.annual.amount,
      monthlyFee: subscriptionData.monthly.amount,
      paymentMethod: '-',
    });
  }, [subscriptionData]);

  // Fetch billing history from Firestore
  useEffect(() => {
    if (!user?.email) return;

    const fetchBillingHistory = async () => {
      setBillingError(null);
      try {
        const [snapByEmail, snapByCustomerEmail] = await Promise.all([
          getDocs(query(
            collection(db, 'tbl_stripe_payment_history'),
            where('email', '==', user.email),
            orderBy('created_at', 'desc'),
            limit(20)
          )),
          getDocs(query(
            collection(db, 'tbl_stripe_payment_history'),
            where('customerEmail', '==', user.email),
            orderBy('created_at', 'desc'),
            limit(20)
          )),
        ]);

        // Combine and deduplicate by doc ID
        const allDocsMap = new Map();
        [...snapByEmail.docs, ...snapByCustomerEmail.docs].forEach((d) => {
          if (!allDocsMap.has(d.id)) allDocsMap.set(d.id, d);
        });
        const allDocs = [...allDocsMap.values()];

        if (allDocs.length > 0) {
          const history = allDocs.map((d) => {
            const data = d.data();
            const date = data.created_at?.toDate
              ? formatDateUtil(data.created_at.toDate())
              : data.created_at
              ? formatDateUtil(new Date(data.created_at))
              : 'N/A';
            const amt = data.amount ?? data.subs_amount ?? 0;
            const numAmt = Number(amt);
            return {
              id: d.id,
              date,
              description: data.subs_description || data.product_details || 'Subscription Payment',
              amount: Number.isFinite(numAmt)
                ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numAmt)
                : String(amt),
              status: data.payment_status === 'Active' ? 'Paid' : data.payment_status || 'Pending',
              receipt: null,
            };
          });
          setBillingHistory(history);
        }
      } catch (error) {
        console.error('Error fetching billing history:', error);
        setBillingError('Failed to load billing history. Please try again later.');
      }
    };

    fetchBillingHistory();
  }, [user?.email]);

  // Keep local Account Status in sync with Firestore `users.status`
  useEffect(() => {
    if (userProfile?.status) {
      console.log('🔍 Account Status Debug:', {
        firebaseStatus: userProfile.status,
        userProfile: userProfile
      });
      
      // Map Firebase status values to UI display values
      const statusMapping = {
        'active': 'Active',
        'inactive': 'Inactive',
        'paused': 'Paused',
        'suspended': 'Suspended'
      };
      const mappedStatus = statusMapping[userProfile.status] || userProfile.status;
      console.log('🔍 Status Mapping:', {
        from: userProfile.status,
        to: mappedStatus
      });
      setAccountStatus(mappedStatus);
    }
  }, [userProfile?.status]);
  
  const handleNotificationToggle = async (type) => {
    // Toggle local state first for responsive UI
    const newValue = !profileData.notifications[type];
    setProfileData({
      ...profileData,
      notifications: {
        ...profileData.notifications,
        [type]: newValue
      }
    });

    // Persist to Firestore immediately when toggled
    try {
      if (type === 'email') {
        const newPrefs = {
          emailNotifications: newValue
        };
        await updateUserProfile({ notificationPreferences: newPrefs });
      }
    } catch (err) {
      console.error('Failed to update notification preference:', err);
    }
  };
  
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const handleChangePassword = async () => {
    if (!user) {
      await Swal.fire({
        title: 'Session Expired',
        text: 'Please sign in again to change your password.',
        icon: 'error',
        customClass: { popup: 'custom-swal-popup', title: 'custom-swal-title', htmlContainer: 'custom-swal-text' },
        background: '#1f2937',
        color: '#e5e7eb',
      });
      navigate('/login');
      return;
    }

    if (isChangingPassword) return;
    setIsChangingPassword(true);

    try {
      const { value: formValues } = await Swal.fire({
        title: 'Change Password',
        html:
          '<div style="text-align:left">' +
          '<label style="display:block;color:#9ca3af;font-size:0.875rem;margin-bottom:4px">Current Password</label>' +
          '<input id="swal-current-password" type="password" autocomplete="current-password" class="swal2-input" placeholder="Current password" style="background:#1f2937;color:#fff;border:1px solid #374151;margin:0 0 12px 0;width:100%;box-sizing:border-box">' +
          '<label style="display:block;color:#9ca3af;font-size:0.875rem;margin-bottom:4px">New Password</label>' +
          '<input id="swal-new-password" type="password" autocomplete="new-password" class="swal2-input" placeholder="New password (min 6 chars)" style="background:#1f2937;color:#fff;border:1px solid #374151;margin:0 0 12px 0;width:100%;box-sizing:border-box">' +
          '<label style="display:block;color:#9ca3af;font-size:0.875rem;margin-bottom:4px">Confirm New Password</label>' +
          '<input id="swal-confirm-password" type="password" autocomplete="new-password" class="swal2-input" placeholder="Confirm new password" style="background:#1f2937;color:#fff;border:1px solid #374151;margin:0;width:100%;box-sizing:border-box">' +
          '</div>',
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Update Password',
        cancelButtonText: 'Cancel',
        customClass: {
          popup: 'custom-swal-popup',
          title: 'custom-swal-title',
          htmlContainer: 'custom-swal-text',
        },
        background: '#1f2937',
        color: '#e5e7eb',
        preConfirm: () => {
          const popup = Swal.getPopup();
          const currentPassword = popup.querySelector('#swal-current-password').value;
          const newPassword = popup.querySelector('#swal-new-password').value;
          const confirmPassword = popup.querySelector('#swal-confirm-password').value;

          if (!currentPassword || !newPassword || !confirmPassword) {
            Swal.showValidationMessage('Please fill in all fields');
            return false;
          }
          if (newPassword.length < 6) {
            Swal.showValidationMessage('New password must be at least 6 characters');
            return false;
          }
          if (newPassword !== confirmPassword) {
            Swal.showValidationMessage('New passwords do not match');
            return false;
          }
          if (newPassword === currentPassword) {
            Swal.showValidationMessage('New password must be different from current password');
            return false;
          }
          return { currentPassword, newPassword };
        }
      });

      if (formValues) {
        await updateUserPassword(formValues.currentPassword, formValues.newPassword);
        await Swal.fire({
          title: 'Password Updated',
          text: 'Your password has been changed successfully.',
          icon: 'success',
          customClass: { popup: 'custom-swal-popup', title: 'custom-swal-title', htmlContainer: 'custom-swal-text' },
          background: '#1f2937',
          color: '#e5e7eb',
        });
      }
    } catch (error) {
      let errorMessage = 'Failed to update password. Please try again.';
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMessage = 'Current password is incorrect.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'New password is too weak. Please use at least 6 characters.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many attempts. Please try again later.';
      } else if (error.code === 'auth/requires-recent-login') {
        errorMessage = 'Session expired. Please sign out and sign back in, then try again.';
      }
      await Swal.fire({
        title: 'Error',
        text: errorMessage,
        icon: 'error',
        customClass: { popup: 'custom-swal-popup', title: 'custom-swal-title', htmlContainer: 'custom-swal-text' },
        background: '#1f2937',
        color: '#e5e7eb',
      });
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleDisconnectBroker = async (integrationId) => {
    console.log('🔍 handleDisconnectBroker called with integrationId:', integrationId);
    const result = await Swal.fire({
      title: 'Disconnect Broker?',
      text: 'Are you sure you want to disconnect this broker account?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, disconnect',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'custom-swal-popup',
        title: 'custom-swal-title',
        htmlContainer: 'custom-swal-text',
      },
      background: '#1f2937', // bg-gray-800 จาก tailwind
      color: '#e5e7eb',       // text-gray-200
    });
  
    if (result.isConfirmed) {
      console.log('🔍 User confirmed disconnect, calling deleteBrokerIntegration...');
      try {
        await deleteBrokerIntegration(integrationId);
        notify({
          title: 'Broker Disconnected',
          message: 'Your broker account has been successfully disconnected.',
          type: 'system'
        });
      } catch (error) {
        console.error('Error disconnecting broker:', error);
        notify({
          title: 'Disconnection Failed',
          message: 'Failed to disconnect broker account. Please try again.',
          type: 'system'
        });
      }
    }
  };

  const handleCancelSubscription = async (rowType) => {
    const result = await Swal.fire({
      title: 'Cancel Subscription?',
      text: 'Are you sure you want to cancel your subscription? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, cancel subscription',
      cancelButtonText: 'Keep subscription',
      confirmButtonColor: '#dc2626', // red-600
      cancelButtonColor: '#6b7280', // gray-500
      customClass: {
        popup: 'custom-swal-popup',
        title: 'custom-swal-title',
        htmlContainer: 'custom-swal-text',
      },
      background: '#1f2937', // bg-gray-800
      color: '#e5e7eb',       // text-gray-200
    });

    if (result.isConfirmed) {
      try {
        // Get subscription info for the API call
        const subData = subscriptionData[rowType];

        // Call backend cancel-subscription endpoint
        const idToken = await user.getIdToken();
        const response = await fetch(
          'https://us-central1-quantum-flo-auto-algo-d3c2b.cloudfunctions.net/cancel_subscription',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
            body: JSON.stringify({
              customerId: user.email,
              reason: 'Customer request via account page',
              tier: subData.tier,
              type: rowType,
            }),
          }
        );

        const data = await response.json();

        if (data.success) {
          // Update UI state
          setSubscriptionData((prev) => ({
            ...prev,
            [rowType]: { ...prev[rowType], paymentStatus: 'Inactive' },
          }));

          await Swal.fire({
            title: 'Subscription Cancelled',
            text: 'Your subscription has been cancelled successfully.',
            icon: 'success',
            confirmButtonColor: '#7c3aed',
            background: '#1f2937',
            color: '#e5e7eb',
          });
        } else {
          throw new Error(data.message || 'Failed to cancel');
        }
      } catch (error) {
        console.error('Cancel subscription error:', error);
        await Swal.fire({
          title: 'Cancellation Failed',
          text: 'Failed to cancel subscription. Please try again or contact support.',
          icon: 'error',
          confirmButtonColor: '#dc2626',
          background: '#1f2937',
          color: '#e5e7eb',
        });
      }
    }
  };

  const getPaymentStatusBadgeClass = (status) => {
    const normalized = (status || '').toString().toLowerCase();
    if (normalized === 'processing' || normalized === 'pending') {
      return 'bg-yellow-900 text-yellow-300';
    }
    if (normalized === 'inactive' || normalized === 'in active' || normalized === 'in-active') {
      return 'bg-red-900 text-red-300';
    }
    if (normalized === 'active' || normalized === 'paid' || normalized === 'success') {
      return 'bg-green-900 text-green-300';
    }
    return 'bg-red-900 text-red-300';
  };

  const handleRefreshBrokers = async () => {
    try {
      await refreshIntegrations();
      notify({
        title: 'Data Refreshed',
        message: 'Broker integration data has been refreshed.',
        type: 'system'
      });
    } catch (error) {
      console.error('Error refreshing broker data:', error);
      notify({
        title: 'Refresh Failed',
        message: 'Failed to refresh broker data. Please try again.',
        type: 'system'
      });
    }
  };
  const handleAddBroker = () => {
    navigate('/broker-connect');
  };

  // Force refresh context data when component mounts or when user changes
  useEffect(() => {
    const refreshContext = async () => {
      try {
        console.log('🔄 AccountPage - Refreshing broker integrations context...');
        await refreshIntegrations();
        console.log('✅ AccountPage - Context refreshed successfully');
      } catch (error) {
        console.error('❌ AccountPage - Failed to refresh context:', error);
      }
    };

    if (user?.uid) {
      refreshContext();
    }
  }, [user?.uid, refreshIntegrations]);

  const handleAccountStatusChange = (integrationId, newStatus) => {
    setAccountStatuses(prev => ({
      ...prev,
      [integrationId]: newStatus
    }));
  };

  const getAccountStatus = (integrationId) => {
    return accountStatuses[integrationId] || 'Active';
  };

  const getAccountStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-900 text-green-300';
      case 'Paused':
        return 'bg-yellow-900 text-yellow-300';
      case 'Terminated':
        return 'bg-red-900 text-red-300';
      default:
        return 'bg-blue-900 text-blue-300';
    }
  };

  const getAccountStatusDotColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-400';
      case 'Paused':
        return 'bg-yellow-400';
      case 'Terminated':
        return 'bg-red-400';
      default:
        return 'bg-blue-400';
    }
  };
  
  // Handle profile input changes
  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // Save profile changes to Firebase
  const handleSaveProfile = async () => {
    setIsSaving(true);
    
    try {
      const updates = {
        fullName: profileData.name,
        phoneNumber: profileData.phone,
        timezone: profileData.timezone,
        status: (() => {
          // Map UI display values back to Firebase values
          const reverseMapping = {
            'Active': 'active',
            'Inactive': 'inactive', 
            'Paused': 'paused',
            'Suspended': 'suspended'
          };
          return reverseMapping[accountStatus] || accountStatus.toLowerCase();
        })(),
        notificationPreferences: {
          emailNotifications: profileData.notifications.email
        }
      };
      
      await updateUserProfile(updates);
      
      // Send success notification
      notify({
        title: 'Profile Updated',
        message: 'Your profile changes have been saved successfully.',
        type: 'system'
      });
      
    } catch (error) {
      console.error('Error saving profile:', error);
      notify({
        title: 'Update Failed',
        message: 'Failed to save profile changes. Please try again.',
        type: 'system'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const primaryBroker = brokerIntegrations?.[0] || null;
  const tierId = String(userProfile?.tier || 'tier1').toLowerCase().replace(/\s+/g, '');
  const tier = getTierConfig(tierId === '2' || tierId === 'tier2' ? 'tier2' : tierId === '3' || tierId === 'tier3' ? 'tier3' : 'tier1');

  const engagementSnapshot = useMemo(() => {
    const engaged = (stocks || []).filter((s) => (s.quanlity || s.value || 0) > 0 || s.status);
    const symbols = engaged.length
      ? engaged.map((s) => s.symbol).filter(Boolean)
      : ['SPY', 'QQQ'];
    const types = engaged.map((s) => String(s.type || '').toLowerCase());
    const tradeType = types.some((t) => t.includes('spread')) || engaged.length === 0 ? 'Spreads' : 'Single-leg';
    const maxPerTrade = engaged.reduce((max, s) => Math.max(max, Number(s.quanlity) || 0), 0) || tier.maxPerTrade;
    const maxSimultaneous = savedMaxSimultaneous ?? MAX_SIMULTANEOUS_DEFAULT;
    const brokerName = primaryBroker ? getBrokerDisplayName(primaryBroker.broker) : '—';
    const accountMask = primaryBroker ? getAccountDisplay(primaryBroker) : '—';
    return {
      brokerLabel: primaryBroker ? `${brokerName} ${accountMask}` : 'No broker connected',
      symbolsLabel: symbols.join(' & '),
      tradeType,
      maxPerTrade,
      maxSimultaneous,
      maxExposure: maxPerTrade * maxSimultaneous,
      tierName: tier.name,
      connected: primaryBroker ? getRealTimeStatus(primaryBroker) === 'connected' : false,
    };
  }, [stocks, savedMaxSimultaneous, primaryBroker, tier.maxPerTrade, tier.name, brokerIntegrations, activeConnections, expiredConnections]);

  const standingAuth = userProfile?.standingAuthorization || null;
  const standingHist = userProfile?.standingAuthorizationHistory || [];
  // ONE clock read and ONE derived state for the whole card. Chris, 2026-08-21:
  // an authorization signed Aug 19 / expiring Aug 20 was still rendering ACTIVE on
  // Aug 21 next to "Days remaining: 0", because this used to be
  // `standingAuth?.status === 'active'` -- the stored string, which nothing ever
  // expires -- and the day count used `setHours(16, ...)`, i.e. 4 PM in the
  // BROWSER's zone, on a card that says 4:00 PM ET. See ./standingAuthDisplay.js;
  // the ET boundary there mirrors functions-ts/src/utils/standingAuthorization.ts
  // so the page and the 4 PM ET cleanup job cannot disagree. Sharing `saNow`
  // across the three calls is what stops the badge and the day count straddling
  // the boundary and contradicting each other again.
  const saNow = new Date();
  const saState = deriveStandingAuthState(standingAuth, saNow);
  const saIsActive = STANDING_AUTH_ENABLED && saState === STANDING_AUTH_STATES.ACTIVE;
  const saIsRevoked = saState === STANDING_AUTH_STATES.REVOKED;
  const saIsExpired = saState === STANDING_AUTH_STATES.EXPIRED;
  const saExpiryEtLabel = standingAuthorizationExpiryLabel(standingAuth);
  // The date the form PROMISES on the same ET clock that will enforce it, so the
  // preview above the signature and the value written on submit cannot differ.
  const saExpiryLabel = standingAuthorizationExpiryForDays(saDays, saNow);
  // The n-day rule spelled out beside the input (Tin, 2026-08-21). Wording only.
  const saDayCountExplanation = standingAuthorizationDayCountExplanation(saDays, saNow);
  // Would this grant already be over before it is written? Signing a 1-day
  // authorization after 4:00 PM ET produced a record that was expired on arrival
  // while the page announced success -- see standingAuthorizationGrantIsVoid.
  // The AUTHORITATIVE check is the one inside handleSignStandingAuth, which reads
  // its own clock: this render-time value can go stale on a form left open across
  // 16:00 ET, and a stale `false` here would re-open exactly the defect.
  const saGrantIsVoid = standingAuthorizationGrantIsVoid(saDays, saNow);
  const allAcksChecked = saAcks.every(Boolean);

  const daysRemaining = daysRemainingInAuthorization(standingAuth, saNow);

  const planLabel = (() => {
    const annualActive = String(subscriptionData.annual.paymentStatus).toLowerCase() === 'active';
    const monthlyActive = String(subscriptionData.monthly.paymentStatus).toLowerCase() === 'active';
    if (monthlyActive && !annualActive) return 'QFAA · Pay As You Go';
    return 'QFAA · Single Pay';
  })();

  const planActive =
    String(subscriptionData.annual.paymentStatus).toLowerCase() === 'active' ||
    String(subscriptionData.monthly.paymentStatus).toLowerCase() === 'active';

  const handleSignStandingAuth = async () => {
    // The card's simultaneous-trades number must be the saved one, not the fallback.
    if (maxSimRead !== 'ok') {
      notify({
        title: 'Authorization not signed',
        message: 'Your saved engagement settings have not loaded yet. Try again in a moment.',
        type: 'system',
      });
      return;
    }
    const n = Math.max(1, parseInt(saDays, 10) || 1);
    // ONE clock read, converted to ET for BOTH fields. `expiry` decides how long
    // this grant holds the 4:00 PM ET engagement clear, so it is derived from the
    // ET calendar day, not the browser's -- otherwise signing at 00:30 in Bangkok
    // (still the previous day in ET) bought a day more than the same click in New
    // York. `signed` is the stamp on a consent record and now carries the real ET
    // wall clock; it used to format LOCAL time and append the string ' ET'.
    const now = new Date();
    // REFUSE A GRANT THAT IS ALREADY OVER. Re-checked here against this handler's
    // own clock, not the render-time value: the form can sit open across 4:00 PM
    // ET, and the whole defect was that the page reported success for a record the
    // server had already stopped honouring. Declining is the conservative choice --
    // rolling the expiry forward would change how many days a signature buys, and
    // Tin confirmed from Chris's history that the n-1 rule stands.
    if (standingAuthorizationGrantIsVoid(n, now)) {
      setSaConfirm('');
      notify({
        title: 'Authorization not signed',
        message:
          `A ${n}-day authorization would end at 4:00 PM ET today, which has already passed, so it would ` +
          'be over the moment it was signed. Choose more days, or sign again tomorrow before 4:00 PM ET.',
        type: 'system',
      });
      return;
    }
    const next = {
      status: 'active',
      days: n,
      expiry: standingAuthorizationExpiryForDays(n, now),
      // The exact 4:00 PM ET boundary as an ISO instant -- BEAM'S PIECE from PR
      // #204 (buildStandingAuthorization). `expiry` above stays because it is the
      // label the card prints and the shape every existing record has; this is what
      // readers enforce off, so nobody re-derives a boundary from display text.
      expiryAt: standingAuthorizationExpiryInstantForDays(n, now),
      signed: standingAuthorizationSignedLabel(now),
      docVersion: SA_DOC_VERSION,
      snapshot: {
        account: engagementSnapshot.brokerLabel,
        symbols: engagementSnapshot.symbolsLabel,
        tradeType: engagementSnapshot.tradeType,
        maxPerTrade: engagementSnapshot.maxPerTrade,
        maxSimultaneous: engagementSnapshot.maxSimultaneous,
        maxExposure: engagementSnapshot.maxExposure,
        tierName: engagementSnapshot.tierName,
      },
    };
    setSaSaving(true);
    try {
      await updateUserProfile({
        standingAuthorization: next,
        standingAuthorizationHistory: [next, ...standingHist].slice(0, 20),
      });
      setSaConfirm(
        `Standing authorization active. Your engagement will remain in force, exactly as configured, until 4:00 PM ET on ${next.expiry}. You can revoke at any time from this page.`
      );
      setTimeout(() => {
        setSaConfirm('');
        setSaAcks([false, false, false, false, false, false]);
      }, 1600);
    } catch (error) {
      console.error('Error signing standing authorization:', error);
      notify({
        title: 'Authorization failed',
        message: 'Could not save standing authorization. Please try again.',
        type: 'system',
      });
    } finally {
      setSaSaving(false);
    }
  };

  const handleRevokeStandingAuth = async () => {
    const ok = window.confirm(
      'Revoking is immediate for new trades. Any open position will still be managed to close. Your account returns to daily engagement today.\n\nRevoke this standing authorization?'
    );
    if (!ok || !standingAuth) return;
    const ended = formatSaDate(new Date());
    const revoked = { ...standingAuth, status: 'revoked', ended };
    setSaSaving(true);
    // Two outcomes have to stay distinguishable (Tin, 2026-08-21): "nothing
    // happened" and "consent is withdrawn but your rows are still there". The
    // second is NOT a failure to report as success -- the client is still engaged
    // until 4:00 PM ET -- and it is not a failure to report as "revoke failed"
    // either, because the consent record really is gone.
    let recordRevoked = false;
    let engagementDeleteFailed = false;
    try {
      await updateUserProfile({
        standingAuthorization: revoked,
        standingAuthorizationHistory: [revoked, ...standingHist.filter((h) => h.signed !== standingAuth.signed)].slice(0, 20),
      });
      recordRevoked = true;
      // BEAM'S PIECE from PR #204: clear the engagement rows NOW. Without this a
      // revoked client stays engaged until the next 4 PM ET clear, while the
      // confirm dialog immediately above promises "Revoking is immediate for new
      // trades". This is what makes that sentence true.
      await deleteEngagementsForUser({
        userId: user?.uid,
        email: userProfile?.email || user?.email,
        username: userProfile?.username,
        // The helper logs and swallows per-query failures so one bad identifier
        // cannot abort the rest; without this hook a total failure is
        // indistinguishable from "you had no rows".
        onError: (error) => {
          engagementDeleteFailed = true;
          console.error('Error clearing engagements after revoke:', error);
        },
      });
    } catch (error) {
      console.error('Error revoking standing authorization:', error);
      if (recordRevoked) engagementDeleteFailed = true;
    } finally {
      setSaSaving(false);
    }
    if (!recordRevoked) {
      notify({
        title: 'Revoke failed',
        message: 'Could not revoke your standing authorization. Nothing has changed. Please try again.',
        type: 'system',
      });
    } else if (engagementDeleteFailed) {
      notify({
        title: 'Revoked, but your engagement was not cleared',
        message:
          'Your standing authorization is revoked, but we could not clear your current engagement. ' +
          'Remove your symbols from the dashboard, or they will clear at the 4:00 PM ET daily reset.',
        type: 'system',
      });
    } else {
      notify({
        title: 'Authorization revoked',
        message: 'Daily engagement is required again. Re-engage from the dashboard before 4:00 PM ET.',
        type: 'system',
      });
    }
  };

  const avatarInitials = (() => {
    if (userProfile?.fullName) {
      const cleanName = userProfile.fullName.replace(/^(Mr\.|Mrs\.|Ms\.|Dr\.|Prof\.)\s*/i, '');
      const names = cleanName.split(' ').filter(Boolean);
      if (names.length >= 2) return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
      if (names.length === 1) return names[0].charAt(0).toUpperCase();
    }
    return userProfile?.username?.charAt(0).toUpperCase() || 'U';
  })();

  const renderProfileTab = () => (
    <div className="card">
      <h2>Personal Information</h2>
      <div className="grid2">
        <div className="fld">
          <label>Full Name</label>
          <input type="text" value={profileData.name} onChange={(e) => handleProfileChange('name', e.target.value)} />
        </div>
        <div className="fld">
          <label>Username</label>
          <input type="text" value={profileData.username} disabled />
          <div className="help">Username cannot be changed</div>
        </div>
        <div className="fld">
          <label>Email Address</label>
          <input type="text" value={profileData.email} disabled />
          <div className="help">Email cannot be changed</div>
        </div>
        <div className="fld">
          <label>Phone Number</label>
          <input type="text" value={profileData.phone} onChange={(e) => handleProfileChange('phone', e.target.value)} />
        </div>
        <div className="fld">
          <label>Timezone</label>
          <select value={profileData.timezone} onChange={(e) => handleProfileChange('timezone', e.target.value)}>
            <option value="America/New_York">Eastern Time (ET)</option>
            <option value="America/Chicago">Central Time (CT)</option>
            <option value="America/Denver">Mountain Time (MT)</option>
            <option value="America/Los_Angeles">Pacific Time (PT)</option>
          </select>
        </div>
      </div>
      <div style={{ marginTop: 26, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="btn purple" onClick={handleSaveProfile} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
        <button type="button" className="btn ghost" onClick={handleChangePassword} disabled={isChangingPassword}>
          {isChangingPassword ? 'Changing...' : 'Change Password'}
        </button>
      </div>
    </div>
  );

  const renderBrokersTab = () => (
    <div className="card">
      <h2>Brokers &amp; Trading</h2>
      <div className="rowline">
        <span className="k">Broker</span>
        <span className="v">
          {engagementSnapshot.brokerLabel}
          {primaryBroker ? (
            <span className={`pillb ${engagementSnapshot.connected ? 'g' : 'r'}`}>
              {engagementSnapshot.connected ? 'Connected' : 'Disconnected'}
            </span>
          ) : null}
        </span>
      </div>
      <div className="rowline">
        <span className="k">Engagement</span>
        <span className="v">
          {saIsActive ? (
            <>
              Engaged — <b>Authorized through {standingAuth.expiry}</b>
              <span className="pillb g">Standing authorization</span>
            </>
          ) : (
            <>
              Engaged — daily mode <span className="pillb a">Expires 4:00 PM ET daily</span>
            </>
          )}
        </span>
      </div>
      <div className="rowline">
        <span className="k">Symbols</span>
        <span className="v">
          {engagementSnapshot.symbolsLabel} · {engagementSnapshot.tradeType}
        </span>
      </div>
      <div className="rowline">
        <span className="k">$Max / Trade</span>
        <span className="v">
          ${engagementSnapshot.maxPerTrade.toLocaleString()} ({engagementSnapshot.tierName}: up to $
          {tier.maxPerTrade.toLocaleString()})
        </span>
      </div>
      <div className="rowline">
        <span className="k">Max simultaneous trades</span>
        <span className="v">{engagementSnapshot.maxSimultaneous}</span>
      </div>
      <div className="row-actions">
        <button type="button" className="btn ghost" onClick={handleAddBroker}>
          Connect Broker
        </button>
        {primaryBroker ? (
          <button type="button" className="btn danger" onClick={() => handleDisconnectBroker(primaryBroker.id)}>
            Disconnect
          </button>
        ) : null}
      </div>
    </div>
  );

  const renderBillingTab = () => (
    <div className="card">
      <h2>Billing &amp; Subscription</h2>
      <div className="rowline">
        <span className="k">Plan</span>
        <span className="v">
          {planLabel}
          <span className={`pillb ${planActive ? 'g' : 'a'}`}>{planActive ? 'Active' : 'Inactive'}</span>
        </span>
      </div>
      <div className="rowline">
        <span className="k">Tier</span>
        <span className="v">
          {tier.name} · up to ${tier.maxPerTrade.toLocaleString()} risk per trade
        </span>
      </div>
      <div className="rowline">
        <span className="k">Trades</span>
        <span className="v">SPY &amp; QQQ (SPX when available)</span>
      </div>
      <div className="row-actions">
        <button type="button" className="btn purple" onClick={() => navigate('/upgrade')}>
          Upgrade Plan
        </button>
      </div>
    </div>
  );

  const renderAuthTab = () => (
    <div>
      <div className="card">
        <h2>Standing Trading Authorization</h2>
        <p className="note">
          By default, your engagement of JTNC&apos;s Quantum Flo Auto Algo expires every trading day at 4:00 PM
          Eastern Time, and you must re-engage each day before the system will trade for you again. This page
          lets you replace that daily confirmation with a standing authorization: a signed grant that keeps
          your current engagement in force, exactly as you configured it, for a number of days you choose.
          Nothing on this page changes what the system trades or how much — those settings are made when you
          engage the algo, and this page only copies them. This page does one thing: it removes the daily
          4:00 PM expiry for the period you authorize.
        </p>
        <p className="note" style={{ marginTop: 10 }}>
          If you do not sign a standing authorization, nothing changes: your engagement will continue to
          expire at 4:00 PM ET each trading day and you will re-engage daily.
        </p>
      </div>

      {saIsActive && daysRemaining <= 7 ? (
        <div className="banner">
          Your standing authorization expires at 4:00 PM ET on {saExpiryEtLabel}. To continue without daily
          re-engagement, sign a new authorization after this one expires.
        </div>
      ) : null}

      {/* The state Chris photographed on 2026-08-21: a grant whose 4:00 PM ET expiry
          has passed. It used to render as the ACTIVE card. Now it says what happened,
          and what restores it -- the sign form below stays on screen for this state. */}
      {saIsExpired ? (
        <div className="banner">
          {saExpiryEtLabel
            ? `Your standing authorization expired at 4:00 PM ET on ${saExpiryEtLabel}.`
            : 'Your standing authorization is no longer in force.'}{' '}
          Your engagement now expires at 4:00 PM ET each trading day, so you have to re-engage daily. Signing a
          new authorization below is what removes the daily step again.
        </div>
      ) : null}

      {saIsActive ? (
        <div className="card">
          <h2>
            Authorization active <span className="pillb g">Active</span>
          </h2>
          <div className="snap">
            <div>
              Signed
              <br />
              <b>{standingAuth.signed}</b>
            </div>
            <div>
              Document version
              <br />
              <b>{standingAuth.docVersion || SA_DOC_VERSION}</b>
            </div>
            <div>
              Expires
              <br />
              {/* The parsed date, not the raw stored string: the card claims this
                  authorization is in force until then, so it must print the date the
                  in-force decision actually used. */}
              <b>4:00 PM ET {saExpiryEtLabel}</b>
            </div>
            <div>
              Days remaining
              <br />
              <b>{daysRemaining}</b>
            </div>
            <div>
              Account
              <br />
              <b>{standingAuth.snapshot?.account || engagementSnapshot.brokerLabel}</b>
            </div>
            <div>
              Symbols
              <br />
              <b>
                {standingAuth.snapshot?.symbols || engagementSnapshot.symbolsLabel} ·{' '}
                {standingAuth.snapshot?.tradeType || engagementSnapshot.tradeType}
              </b>
            </div>
            <div>
              $Max / Trade
              <br />
              <b>
                ${Number(standingAuth.snapshot?.maxPerTrade || engagementSnapshot.maxPerTrade).toLocaleString()} (
                {standingAuth.snapshot?.tierName || engagementSnapshot.tierName})
              </b>
            </div>
            <div>
              Max simultaneous / exposure
              <br />
              <b>
                {standingAuth.snapshot?.maxSimultaneous || engagementSnapshot.maxSimultaneous} · $
                {Number(standingAuth.snapshot?.maxExposure || engagementSnapshot.maxExposure).toLocaleString()}
              </b>
            </div>
          </div>
          {/* Chris asked for this twice: item 1 of his 2026-08-17 list (a one-day grant
              already ends at 4:00 PM ET today, so there is nothing left to stop) and the
              2026-08-21 screenshot, where Revoke sat under an authorization that had
              expired the day before. Shown only while the grant is genuinely in force.
              Same `saNow` as the state above, so the two cannot straddle the boundary. */}
          {showRevokeStandingAuthorization(standingAuth, saNow) ? (
            <div style={{ marginTop: 18 }}>
              <button type="button" className="btn danger" onClick={handleRevokeStandingAuth} disabled={saSaving}>
                Revoke Standing Authorization
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {standingAuth && !saIsActive ? (
        <div className="card">
          {/* Badge and copy both read `saState`, not the stored status string: an
              authorization past its 4:00 PM ET expiry still says status 'active' in
              Firestore, and calling that "active" anywhere on this page is the bug. */}
          <h2>
            Prior authorization <span className="pillb r">{saIsRevoked ? 'Revoked' : 'Expired'}</span>
          </h2>
          <p className="note">
            {saIsRevoked
              ? `Revoked by you${standingAuth.ended ? ` ${standingAuth.ended}` : ''}.`
              : saExpiryEtLabel
                ? `Expired at 4:00 PM ET on ${saExpiryEtLabel}.`
                : 'This authorization is no longer in force.'}{' '}
            Your engagement expires at 4:00 PM ET each trading day again. Sign a new authorization below to
            keep it in force without the daily step.
          </p>
          {standingHist.length > 0 ? (
            <div className="hist" style={{ marginTop: 10 }}>
              History:{' '}
              {standingHist.map((h, i) => (
                <span key={`${h.signed}-${i}`}>
                  {i > 0 ? <br /> : null}
                  {h.status} — signed {h.signed} · expiry 4:00 PM ET {h.expiry}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {!saIsActive ? (
        <div className="card">
          <h2>Your current engagement (read-only)</h2>
          <div className="snap">
            <div>
              Account
              <br />
              <b>{engagementSnapshot.brokerLabel}</b>
            </div>
            <div>
              Symbols
              <br />
              <b>{engagementSnapshot.symbolsLabel}</b>
            </div>
            <div>
              Type
              <br />
              <b>{engagementSnapshot.tradeType}</b>
            </div>
            <div>
              $Max / Trade
              <br />
              <b>
                ${engagementSnapshot.maxPerTrade.toLocaleString()} ({engagementSnapshot.tierName}: up to $
                {tier.maxPerTrade.toLocaleString()})
              </b>
            </div>
            <div>
              Max simultaneous trades
              <br />
              <b>{engagementSnapshot.maxSimultaneous}</b>
            </div>
            <div>
              Max concurrent exposure
              <br />
              <b>
                ${engagementSnapshot.maxExposure.toLocaleString()} (${engagementSnapshot.maxPerTrade.toLocaleString()}{' '}
                × {engagementSnapshot.maxSimultaneous})
              </b>
            </div>
          </div>
          <div className="okline">Risk Disclosure &amp; Waiver of Liability — current version accepted.</div>

          <div className="dur">
            Keep this engagement in force, without daily re-confirmation, for
            <input
              type="number"
              min={1}
              step={1}
              value={saDays}
              onChange={(e) => setSaDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
            />{' '}
            days.
          </div>
          <div className="dur-exp">This authorization will expire at 4:00 PM ET on {saExpiryLabel}.</div>
          {/* The rule in plain words, next to the input that sets it (Tin, 2026-08-21).
              The computed date above already discloses it; this says WHY that is the
              date, so "2 days" ending tomorrow is not a surprise. Wording only. */}
          {saDayCountExplanation ? <div className="dur-exp">{saDayCountExplanation}</div> : null}
          {saGrantIsVoid ? (
            <div className="dur-exp">
              <b>
                That window has already closed today. A grant ending at 4:00 PM ET today cannot be signed
                after 4:00 PM ET — it would be over the moment you signed it. Choose more days, or sign
                again tomorrow before 4:00 PM ET.
              </b>
            </div>
          ) : null}

          <div className="agree">
            <h4>STANDING TRADING AUTHORIZATION — JTNC&apos;S QUANTUM FLO AUTO ALGO</h4>
            <p>
              This authorization incorporates, and does not replace, the Risk Disclosure &amp; Waiver of
              Liability that you have separately reviewed and accepted, including its provisions on general
              trading risk, the fallibility of technology, market unpredictability, the absence of any
              guarantee of profit, and your sole responsibility for your account. If you have not accepted
              the current version of the Risk Disclosure, you will be asked to do so, including watching the
              disclosure video in its entirety, before this authorization can be signed.
            </p>
            <h4>1. What you are authorizing.</h4>
            <p>
              You authorize JTNC Group, LLC&apos;s Quantum Flo Auto Algo to enter, manage, and exit trades in
              your connected brokerage account identified above, autonomously and without further daily
              confirmation from you, strictly within the engagement parameters shown above for the symbols,
              instrument type, per-trade maximum, and simultaneous-position maximum that you yourself
              configured, for the duration you select, beginning the day you sign this document and expiring
              at 4:00 PM Eastern Time on the final authorized day.
            </p>
            <h4>2. What this authorization does not do.</h4>
            <p>
              It does not change any trading parameter. It does not permit the system to trade any symbol,
              size, instrument, or position count beyond what is shown above. It does not renew itself: when
              it expires, the system reverts to daily engagement and JTNC will not trade for you until you
              either re-engage for the day or sign a new standing authorization.
            </p>
            <h4>3. Your parameters are locked to this signature.</h4>
            <p>
              This authorization applies only to the engagement configuration shown above, exactly as shown.
              If you change your engagement in any way, for example, symbols, size, instrument type,
              simultaneous-position count, or connected account, this authorization is automatically void,
              your account immediately returns to daily engagement, and you must sign a new standing
              authorization reflecting the new configuration if you want standing authority to continue.
            </p>
            <h4>4. Expiry and open positions.</h4>
            <p>
              The system trades intraday and does not hold positions overnight. For the avoidance of doubt:
              upon expiry or revocation of this authorization, no new trades will be entered, but authority
              to manage and close any position already open survives until that position is flat. Expiry or
              revocation never leaves an open position unmanaged.
            </p>
            <h4>5. Revocation.</h4>
            <p>
              You may revoke this authorization at any time using the Revoke button on this page. Revocation
              is effective immediately for new trades. You may also pause or disable trading at any time
              through your brokerage interface, as described in the Risk Disclosure.
            </p>
            <h4>6. No advice; subscription tiers.</h4>
            <p>
              Your per-trade maximum is limited by your subscription Tier ({tier.name}: up to $
              {tier.maxPerTrade.toLocaleString()} per trade). Subscription tiers are pricing levels only;
              they are not a recommendation, an assessment of suitability, or advice of any kind. JTNC Group,
              LLC is not a licensed financial advisor, and nothing in this authorization is investment
              advice.
            </p>
            <h4>7. Notices.</h4>
            <p>
              We will attempt to notify you approximately seven days before this authorization expires via
              email and at the contact details in your Profile. Delivery of notices is not guaranteed, and
              non-receipt of a notice does not extend this authorization. You are responsible for knowing
              when your authorization expires; the expiry date is displayed on this page at all times.
            </p>
            <h4>8. Everything else is unchanged.</h4>
            <p>
              All acknowledgments you made in the Subscription Agreement and Risk Disclosure &amp; Waiver of
              Liability continue to apply in full while this authorization is in force, including that you
              may lose some or all of your capital, that you remain solely responsible for monitoring your
              account, and that JTNC Group, LLC and its affiliates are released from liability as stated
              there.
            </p>
          </div>

          <label className="ack">
            <input
              type="checkbox"
              checked={saAcks[0]}
              disabled={maxSimRead !== 'ok'}
              onChange={(e) => {
                const next = [...saAcks];
                next[0] = e.target.checked;
                setSaAcks(next);
              }}
            />
            <span>I have read this Standing Trading Authorization in full and I understand it.</span>
          </label>
          <label className="ack">
            <input
              type="checkbox"
              checked={saAcks[1]}
              disabled={maxSimRead !== 'ok'}
              onChange={(e) => {
                const next = [...saAcks];
                next[1] = e.target.checked;
                setSaAcks(next);
              }}
            />
            <span>
              I understand that by signing, the JTNC will trade my account each trading day within the
              parameters shown above, without any further daily confirmation from me, until{' '}
              <b>{saExpiryLabel} 4:00 PM ET</b> unless I revoke sooner.
            </span>
          </label>
          <label className="ack">
            <input
              type="checkbox"
              checked={saAcks[2]}
              disabled={maxSimRead !== 'ok'}
              onChange={(e) => {
                const next = [...saAcks];
                next[2] = e.target.checked;
                setSaAcks(next);
              }}
            />
            <span>
              I understand my maximum concurrent exposure under this authorization is{' '}
              <b>${engagementSnapshot.maxExposure.toLocaleString()}</b> as stated above.
            </span>
          </label>
          <label className="ack">
            <input
              type="checkbox"
              checked={saAcks[3]}
              disabled={maxSimRead !== 'ok'}
              onChange={(e) => {
                const next = [...saAcks];
                next[3] = e.target.checked;
                setSaAcks(next);
              }}
            />
            <span>
              I understand that changing any engagement setting voids this authorization and returns my
              account to daily engagement.
            </span>
          </label>
          <label className="ack">
            <input
              type="checkbox"
              checked={saAcks[4]}
              disabled={maxSimRead !== 'ok'}
              onChange={(e) => {
                const next = [...saAcks];
                next[4] = e.target.checked;
                setSaAcks(next);
              }}
            />
            <span>
              I understand this authorization does not renew automatically and that I must sign again after
              it expires.
            </span>
          </label>
          <label className="ack">
            <input
              type="checkbox"
              checked={saAcks[5]}
              disabled={maxSimRead !== 'ok'}
              onChange={(e) => {
                const next = [...saAcks];
                next[5] = e.target.checked;
                setSaAcks(next);
              }}
            />
            <span>I have reviewed and accepted the current Risk Disclosure &amp; Waiver of Liability.</span>
          </label>

          <div style={{ marginTop: 18 }}>
            <button
              type="button"
              className="btn purple"
              disabled={!allAcksChecked || saSaving || saGrantIsVoid || maxSimRead !== 'ok'}
              onClick={handleSignStandingAuth}
            >
              {saSaving ? 'Saving...' : 'Sign and Activate Standing Authorization'}
            </button>
          </div>
          {maxSimRead === 'error' ? (
            <div className="banner" style={{ marginTop: 12 }}>
              Could not load your saved engagement settings, so the authorization cannot be signed yet.{' '}
              <button type="button" className="btn ghost" onClick={() => setMaxSimAttempt((a) => a + 1)}>
                Retry
              </button>
            </div>
          ) : null}
          {saConfirm ? <p className="okline">{saConfirm}</p> : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <PageLayout>
      <div className="qfaa-account-page">
        <div className="outer">
          <div className="head">
            <h1>Account Settings</h1>
            <div className="avatar">{avatarInitials}</div>
          </div>

          <div className="tabbar">
            <button type="button" className={activeTab === 'profile' ? 'on' : ''} onClick={() => setActiveTab('profile')}>
              Profile
            </button>
            <button type="button" className={activeTab === 'brokers' ? 'on' : ''} onClick={() => setActiveTab('brokers')}>
              Brokers &amp; Trading
            </button>
            <button type="button" className={activeTab === 'billing' ? 'on' : ''} onClick={() => setActiveTab('billing')}>
              Billing &amp; Subscription
            </button>
            {STANDING_AUTH_ENABLED && (
              <button type="button" className={activeTab === 'authorization' ? 'on' : ''} onClick={() => setActiveTab('authorization')}>
                Trading Authorization
              </button>
            )}
          </div>

          {activeTab === 'profile' && renderProfileTab()}
          {activeTab === 'brokers' && renderBrokersTab()}
          {activeTab === 'billing' && renderBillingTab()}
          {STANDING_AUTH_ENABLED && activeTab === 'authorization' && renderAuthTab()}
        </div>
      </div>
    </PageLayout>
  );
}
